import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useAppContext } from "@context/app";
import { COURSE_TYPE, Source, StepInfo } from "@types";
import { useImageFallback } from "@shared/hooks/useImageFallback";
import {
  resolveProductImageSources,
  resolveThumbnailSources,
} from "utils/product-image";
import { useRouter } from "next/router";
import courseApi from "@api/course";
import { handleVideoSources, hasCameFromInternalLink } from "@utils/player";
import EmbedFixedHeightPlayer from "./EmbedFixedHeightPlayer";
import { useEmbedVideoAccess } from "./useEmbedVideoAccess";
import {
  EMBED_PLAYER_FRAME_CLASSNAME,
  getEmbedPlayerFrameStyle,
  parseEmbedPlayerMaxHeight,
} from "@features/video-course/components/embeded-skeleton";

const isQueryEnabled = (value: string | string[] | undefined) => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return false;

  const normalized = raw.trim().toLowerCase();
  return normalized === "1" || normalized === "true";
};

const EmbeddedVideoPlayer: React.FC = () => {
  const { courseData, onEnd, timeUpdateHandler, mutate } =
    useEmbedVideoAccess();

  const {
    user: { isLoggedIn },
  } = useAppContext();
  const { query } = useRouter();

  const isVideoCourse =
    courseData?.product?.attribute_type === COURSE_TYPE.VIDEO;
  const { controls, poster, mute, quality, autoplay, start, height } = query;
  const maxHeight = parseEmbedPlayerMaxHeight(height);
  const theLesson = isVideoCourse
    ? courseData.lessons.find((lesson) => lesson.note === "openCourse") ||
      courseData.lessons[0]
    : courseData.original_lessons[0]
      ? courseData.original_lessons[0]?.steps[0].info
      : courseData.lessons[0];
  const src_720 =
    (theLesson as StepInfo)?.online_link_720 ||
    (theLesson as StepInfo)?.online_link;

  const src_480 = (theLesson as StepInfo)?.online_link_480;

  const useInternalPoster = query.poster_type === "internal";
  const posterChain = useMemo(() => {
    const product = courseData?.product;
    if (!product) return [];
    if (useInternalPoster) return resolveThumbnailSources(product).chain;
    return resolveProductImageSources({
      svg_cover: product.svg_cover || product.svg_image,
      png_cover: product.png_cover || product.png_image,
    }).chain;
  }, [courseData?.product, useInternalPoster]);

  const sourcesList =
    handleVideoSources({
      prev: [],
      isVideoCourse,
      src_480,
      src_720,
      theLesson: theLesson as StepInfo,
    }) || [];

  const [sources, setSources] = useState<Source[]>(sourcesList);

  const posterImage = useImageFallback(posterChain, undefined, posterChain[0]);

  useEffect(() => {
    setSources((prev) => {
      return (
        handleVideoSources({
          prev,
          isVideoCourse,
          src_480,
          src_720,
          theLesson: theLesson as StepInfo,
        }) || []
      );
    });
  }, [isVideoCourse, src_480, src_720, theLesson, quality]);

  const startingPercent = useMemo(() => {
    const durationInNumber = isVideoCourse
      ? courseData.lessons?.[0]?.duration
        ? Number(courseData.lessons[0].duration)
        : null
      : Number((theLesson as StepInfo).duration);
    const queryStartingPoint =
      start && Number(start) && durationInNumber
        ? (Math.abs(Number(start)) * 100) / durationInNumber
        : undefined;
    return queryStartingPoint || undefined;
  }, [courseData, query?.start]);

  const startPlayHandler = async () => {
    if (isLoggedIn && courseData?.product?.id && !courseData?.user?.is_buy) {
      await courseApi.postVideoCourseRegister(courseData?.product?.id).fetch();
      mutate();
    }
  };
  const shouldAutoplay = isQueryEnabled(autoplay);
  const shouldMute = isQueryEnabled(mute);
  const autoPlayOnMount =
    Boolean(query?.start && hasCameFromInternalLink()) || shouldAutoplay;

  if (src_720 || src_480) {
    return (
      <EmbedFixedHeightPlayer
        sources={sources}
        onEnd={onEnd}
        poster={!poster || poster === "1" ? posterImage || "" : ""}
        timeUpdateHandler={timeUpdateHandler}
        id={courseData.product.id}
        subtitleSrc={(theLesson as StepInfo).subtitle || ""}
        onPlay={startPlayHandler}
        autoPlayOnMount={autoPlayOnMount}
        startFrom={startingPercent}
        withControls={controls !== "0"}
        defaultMuted={shouldMute}
        maxHeight={maxHeight}
      />
    );
  }

  return (
    <div
      className={EMBED_PLAYER_FRAME_CLASSNAME}
      style={getEmbedPlayerFrameStyle(maxHeight)}
    >
      <div className="absolute inset-0">
        {posterImage && (
          <div className="relative h-full w-full">
            <Image fill alt="Cover" src={posterImage} priority quality={100} />
          </div>
        )}
      </div>
    </div>
  );
};

EmbeddedVideoPlayer.displayName = "EmbeddedVideoPlayer";

export default EmbeddedVideoPlayer;
